import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { isProjectManager } from "../../../utils/constants/accountType";

const fmtISO = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

/* ── Key-value row (matches invoice / receivable pages) ── */
const Field = ({ label, value, children }) => (
  <div className="flex items-start">
    <span className="w-52 text-sm font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-sm text-gray-900">{children ?? value ?? "—"}</span>
  </div>
);

/* ── Bold inline section header ── */
const SectionHeader = ({ title }) => (
  <p className="text-base font-bold text-gray-900 pt-3 pb-1">{title}</p>
);

/* ── Card section (Payment Method / Linked Invoices / Messages) ── */
const Section = ({ title, count, actionBtn, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
      <span className="text-xl font-bold text-gray-900">{title}</span>
      {actionBtn}
    </div>
    {count !== undefined && (
      <p className="text-xs text-gray-500 px-5 pt-3">
        Total <span className="font-semibold">{count}</span>{" "}
        {count === 1 ? "item" : "items"}.
      </p>
    )}
    {children}
  </div>
);

const ClientPaymentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const readOnly = isProjectManager(user);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const res = await api.get(`/project-finances/client-payments/${id}`, {
          withCredentials: true,
        });
        setPayment(res.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load payment details.");
      } finally {
        setLoading(false);
      }
    };
    fetchPayment();
  }, [id]);

  const handleDelete = async () => {
    if (!await confirmDialog("Delete this payment? This cannot be undone.")) return;
    try {
      await api.delete(`/project-finances/client-payments/${id}`, {
        withCredentials: true,
      });
      navigate("/client/payments");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete payment.");
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
      </div>
    );
  if (error)
    return <p className="text-center mt-10 text-red-600 font-medium">{error}</p>;
  if (!payment)
    return <p className="text-center mt-10 text-gray-500">Payment not found.</p>;

  /* ── derived values ── */
  const getClientName = () => {
    const c = payment.client;
    if (!c) return "—";
    if (c.type?.toLowerCase() === "company") return c.company_name || "—";
    if (c.primary_user)
      return `${c.primary_user.first_name} ${c.primary_user.last_name}`.trim();
    return "—";
  };

  const currencyCode = payment.currency?.currency?.code || "—";
  const legalEntity = payment.client?.legal_entity || "—";
  const createdByName =
    payment.createdByAdmin?.details?.company_name ||
    payment.createdByAdmin?.username ||
    "—";

  /* ── payment method ── */
  const pm = payment.paymentMethod;
  const pmTypeFmt = pm?.payment_method?.replace(/_/g, " ") || "—";

  let pmName = "—";
  let pmDetails = "—";

  if (pm?.bank_transfer_detail) {
    const bt = pm.bank_transfer_detail;
    pmName = bt.payment_method_name || "—";
    const parts = [];
    if (bt.bank_name) parts.push(`Bank name: ${bt.bank_name}`);
    if (bt.bank_address) parts.push(`Bank address: ${bt.bank_address}`);
    if (bt.swift) parts.push(`SWIFT: ${bt.swift}`);
    if (bt.iban) parts.push(`IBAN: ${bt.iban}`);
    if (bt.ifsc_code) parts.push(`IFSC: ${bt.ifsc_code}`);
    if (bt.account_number) parts.push(`Account number: ${bt.account_number}`);
    if (bt.beneficiary_name) parts.push(`Account beneficiary name: ${bt.beneficiary_name}`);
    if (bt.beneficiary_address) parts.push(`Account beneficiary address: ${bt.beneficiary_address}`);
    if (bt.country) parts.push(`Country: ${bt.country}`);
    pmDetails = parts.join("\n") || "—";
  } else if (pm?.email_payment_detail) {
    const ep = pm.email_payment_detail;
    pmName = ep.account_holder_name || ep.email || "—";
    pmDetails = ep.email || "—";
  } else if (pm?.other_payment_detail) {
    pmName = pm.other_payment_detail.payment_method_name || "—";
    pmDetails = "—";
  }

  const linkedInvoice = payment.linkedInvoice;

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="text-2xl font-bold text-gray-900">
            Payment {payment.payment_code}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.error("Download as PDF not yet implemented.")}
            className="bg-gray-600 text-white text-sm px-4 py-2 rounded shadow hover:bg-gray-700"
          >
            Download as PDF
          </button>
          {!readOnly && (
            <>
              <button
                onClick={() => toast.error("Update not yet implemented.")}
                className="bg-blue-600 text-white text-sm px-4 py-2 rounded shadow hover:bg-blue-700"
              >
                Update
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white text-sm px-4 py-2 rounded shadow hover:bg-red-700"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Main detail card ── */}
      <div className="bg-white shadow rounded-lg p-8 space-y-3 text-sm">
        <Field label="Client">
          {payment.client ? (
            <Link
              to={`/clients/${payment.client.id}`}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {getClientName()}
            </Link>
          ) : (
            "—"
          )}
        </Field>
        <Field label="Amount">
          <strong>{fmtNum(payment.amount)}</strong>
        </Field>
        <Field label="Currency" value={currencyCode} />
        <Field label="Paid at" value={fmtISO(payment.paid_at)} />

        <SectionHeader title="Coverage by invoices" />
        <Field label="Coverage by invoices">
          {payment.coverage_by_invoices ? (
            <span className="text-green-600 font-medium">
              {payment.coverage_by_invoices}
            </span>
          ) : (
            "—"
          )}
        </Field>

        <SectionHeader title="Other details" />
        <Field label="Legal entity" value={legalEntity} />
        <Field label="Note" value="—" />
        <Field label="Created at" value={`${fmtISO(payment.createdAt)} | ${createdByName}`} />
      </div>

      {/* ── Payment Method ── */}
      <Section title="Payment Method">
        <div className="px-8 py-5 space-y-3">
          <Field label="Name" value={pmName} />
          <Field label="Type">
            <span className="capitalize">{pmTypeFmt}</span>
          </Field>
          <Field label="Details">
            <span className="whitespace-pre-line">{pmDetails}</span>
          </Field>
        </div>
      </Section>

      {/* ── Linked Invoices ── */}
      <Section
        title="Linked Invoices"
        count={linkedInvoice ? 1 : 0}
      >
        <div className="overflow-x-auto px-5 pb-4 pt-2">
          <table className="w-full text-xs border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-200">
                {[
                  "Code",
                  "Linked payments",
                  "Coverage by payments",
                  "Total after write off",
                  "Amount covered by linked payments",
                  "Amount covered by this payment",
                  "Amount due",
                  "Issued at",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linkedInvoice ? (
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <Link
                      to={`/client/invoice/${linkedInvoice.id}`}
                      className="text-teal-600 hover:text-teal-800 underline font-medium"
                    >
                      {linkedInvoice.invoice_code}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <Link
                      to={`/client/payment/${payment.id}`}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      {payment.payment_code}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <span className="text-green-600 font-medium">
                      {linkedInvoice.coverage_by_payments || "—"}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-right">{fmtNum(linkedInvoice.total)}</td>
                  <td className="py-2 pr-4 text-right">{fmtNum(linkedInvoice.paid_amount)}</td>
                  <td className="py-2 pr-4 text-right">{fmtNum(payment.amount)}</td>
                  <td className="py-2 pr-4 text-right">{fmtNum(linkedInvoice.due_amount)}</td>
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-600">
                    {fmtISO(linkedInvoice.issued_at)}
                  </td>
                  <td className="py-2 whitespace-nowrap">
                    <button
                      onClick={() => toast.error("Unlink not yet implemented.")}
                      className="text-xs text-white bg-red-500 hover:bg-red-600 px-2.5 py-1 rounded"
                    >
                      Unlink
                    </button>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={9} className="py-3 text-xs text-gray-400">
                    No results found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── Messages ── */}
      <Section title="Messages">
        <div className="px-5 py-4 space-y-3">
          <textarea
            rows={4}
            placeholder="Write a message..."
            className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button className="bg-blue-600 text-white text-sm px-4 py-2 rounded shadow hover:bg-blue-700">
            Send message to client
          </button>
        </div>
      </Section>
    </div>
  );
};

export default ClientPaymentDetailPage;
